import {
  Box,
  Button,
  Flex,
  FormControl,
  Heading,
  Image,
  Stack,
  Text,
  useColorMode,
  useMediaQuery,
} from '@chakra-ui/react';
import { conversationAction } from 'actions';
import introductionIcon from 'assets/image/introduction.png';
import Textarea from 'components/Textarea';
import { useFormik } from 'formik';
import { SET_TIMEOUT_STREAMING_ANSWER } from 'helpers/constant';
import { CONVERSATION_STATUS, VIEW_MODE } from 'helpers/enum';
import { getUserInfoStorage } from 'helpers/lib';
import { useEffect, useRef } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiSidebarSimpleBold } from 'react-icons/pi';
import { RiSendPlaneFill } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { ConversationService } from 'services/conversation/conversation.service';
import { Citation } from 'types/reducers/conversation';

import Message from './Message';

interface ChatProps {
  onOpenFile: (_citation: Citation) => void;
  onAddNew: () => void;
  onOpenSidebar: () => void;
}

const Chat = ({ onOpenFile, onAddNew, onOpenSidebar }: ChatProps) => {
  const { t } = useTranslation();
  const chatRef = useRef<HTMLDivElement>(null);
  const cancelEditRef = useRef<HTMLButtonElement>(null);
  const userInfo = getUserInfoStorage();
  const [isMobile] = useMediaQuery('(max-width: 30em)');
  const [isTablet] = useMediaQuery('(max-width: 48em)');
  let timer: ReturnType<typeof setTimeout> | any;
  const { colorMode } = useColorMode();

  const dispatch = useDispatch();
  const { status, current, viewMode, isLimitReached, processingStreamingChunkStatus } = useSelector(
    (state) => state.conversations
  );

  const isChatViewMode = viewMode === VIEW_MODE.CHAT;
  const isGenerateAnswer = status === CONVERSATION_STATUS.GENERATE_ANSWER;
  const isDisableQuestionBox =
    !current?.conversation_id ||
    isGenerateAnswer ||
    isLimitReached ||
    Boolean(current.answerChunks.chunk);

  const setRefBoxHeight = processingStreamingChunkStatus ? '70px' : '';

  const { values, handleChange, handleSubmit, resetForm } = useFormik({
    initialValues: {
      question: '',
    },
    onSubmit: (values) => {
      if (values.question && values.question?.trim()) {
        if (current?.conversation_id) {
          dispatch(
            conversationAction.addNewQuestion({
              ...values,
              created_at: new Date().toISOString(),
            })
          );
          dispatch(conversationAction.updateStatus(CONVERSATION_STATUS.GENERATE_ANSWER));
          dispatch(conversationAction.updateProcessConversationId(current.conversation_id));
          dispatch(conversationAction.updateProcessingStreamingChunkStatus(true));
          dispatch(conversationAction.setTimeOutForFinalAnswer(true));
        }
        resetForm();
      }
      cancelEditRef?.current?.click();
    },
  });

  const isDisableSubmit = !values.question || isGenerateAnswer || isLimitReached;

  const onChangeMessage = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(conversationAction.updateViewMode(VIEW_MODE.CHAT));
    handleChange(e);
  };

  useEffect(() => {
    if (current.setTimeoutFinalAnswer && current.conversation_id) {
      timer = setTimeout(async () => {
        const response = await ConversationService.getLatestMessageConversation({
          conversation_id: current.conversation_id!,
        });

        if (response) {
          dispatch(
            conversationAction.updateNewAnswer({
              conversation_id: response.conversation_id,
              answer: response.answer,
              citations: response.citations,
              timestamp: response.updated_at,
              status: 'IDLE',
            })
          );
          dispatch(conversationAction.setTimeOutForFinalAnswer(false));
          dispatch(conversationAction.updateProcessingStreamingChunkStatus(false));
          dispatch(conversationAction.clearStreamingAnswerChunks());
        } else {
          dispatch(conversationAction.setTimeOutForFinalAnswer(false));
          dispatch(conversationAction.updateProcessingStreamingChunkStatus(false));
          dispatch(conversationAction.clearStreamingAnswerChunks());
        }
      }, SET_TIMEOUT_STREAMING_ANSWER);
    }

    return () => clearTimeout(timer);
  }, [current.setTimeoutFinalAnswer, current.conversation_id]);

  useEffect(() => {
    const scrollToBottom = () => {
      const chatElement = document.getElementById('message-container');
      if (chatElement) {
        chatElement.scrollTo({
          top: chatElement.scrollHeight,
          behavior: 'smooth',
        });
      }
    };

    scrollToBottom();

    return () => {};
  }, [current, status, current.answerChunks.chunk]);

  return (
    <Box
      pos='relative'
      flex={1}
      w={isChatViewMode ? '100%' : ''}
      maxW={{ md: `430px`, lg: `600px`, xl: `800px`, '2xl': `1100px` }}
      h='100%'
      bg='bg_main_container'
      borderRadius={{ lg: '20px' }}
      shadow={{ lg: 'shadow_box' }}
      style={{
        overflow: 'hidden',
      }}
    >
      {!current?.conversation_id ? (
        <Flex align='center' justify='center' h='100%'>
          <Flex align='center' direction='column' w='712px' mb={20}>
            <Image boxSize='64px' mb='16px' alt='introduction' src={introductionIcon} />
            <Box mb='26px' color='color_main_text' textAlign='center'>
              <Heading as={'h2'} my={2} size={{ base: 'md', lg: 'lg' }}>
                {t('chat.welcome')}
              </Heading>
              <Text fontSize={{ base: 'small', md: 'medium' }}>{t('chat.description')}</Text>
            </Box>
            <Button
              w={{ base: '250px', md: '300px' }}
              h={{ base: '52px', md: '64px' }}
              color='#ffffff'
              fontSize={{ base: 'medium', md: '20px' }}
              borderRadius='30px'
              onClick={onAddNew}
              variant='submit'
            >
              {t('chat.start')}
            </Button>
          </Flex>
        </Flex>
      ) : (
        <Box
          ref={chatRef}
          sx={{
            '&::-webkit-scrollbar': {
              display: isMobile ? 'none' : 'block',
            },
          }}
          overflowY='auto'
          h='calc(100% - 100px)'
          id='message-container'
        >
          <Box maxW={{ base: '100%', lg: '1100px' }} m='auto'>
            <Stack direction='column' pt={{ base: 5, lg: 'auto' }} spacing={{ lg: '2rem' }}>
              {current?.data?.map((message, index: number) => (
                <Message
                  isLast={index === current.data.length - 1}
                  key={index}
                  data={message}
                  status={status}
                  onOpenFile={onOpenFile}
                  user={userInfo!}
                  cancelEditRef={cancelEditRef}
                />
              ))}
              <Box h={setRefBoxHeight}></Box>
            </Stack>
          </Box>
        </Box>
      )}

      <Box
        pos='absolute'
        right='0'
        bottom={{ base: '5px', md: '14px' }}
        left='0'
        maxW='1100px'
        m='auto'
      >
        <form onSubmit={handleSubmit}>
          <FormControl>
            <Box
              pos='relative'
              alignItems={'center'}
              justifyContent='center'
              gap={1}
              display={{ base: 'flex', md: 'block' }}
              w='100%'
              m='auto'
              px={8}
            >
              <Button
                display={{ base: 'block', md: 'none' }}
                h={'54px'}
                minH={'52px'}
                color='grey.50'
                onClick={onOpenSidebar}
                variant='submit'
              >
                <PiSidebarSimpleBold />
              </Button>
              <Textarea
                name='question'
                placeholder={t('common.inputPlaceholder')}
                minH='52px'
                maxH={isTablet ? '54px' : 'none'}
                pe={12}
                fontSize={{ base: 'small', md: 'medium', lg: 'large' }}
                maxExpandHeight={200}
                isDisabled={isDisableQuestionBox}
                value={values.question}
                onChange={onChangeMessage}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }
                }}
              />
              {!isDisableSubmit && (
                <Image
                  as={RiSendPlaneFill}
                  pos='absolute'
                  zIndex={999}
                  right={12}
                  bottom={4}
                  boxSize={8}
                  color={colorMode === 'light' ? 'main_color' : 'grey.50'}
                  _hover={{ opacity: '0.7' }}
                  cursor='pointer'
                  onClick={() => handleSubmit()}
                />
              )}
            </Box>
          </FormControl>
        </form>
      </Box>
    </Box>
  );
};

export default Chat;
